import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { AlertProps } from '@business/interfaces/alert';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { signUpCodeSchema } from '@utils/validations/signup';
import { useForm } from 'react-hook-form';
import { useVerifySignupCodeMutation } from '@ports/internal-services/user/userSlice';
import { Input } from '../Input';
import { Button } from '../Button';
import { Alert } from '../Alert';

export const SignUpCode = ({
  setSignUpCode
}: {
  setSignUpCode: (code: string) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertProps | null>(null);

  const [verifySignupCode] = useVerifySignupCodeMutation();

  const {
    register,
    formState: { errors },
    handleSubmit
  } = useForm({
    resolver: yupResolver(signUpCodeSchema)
  });

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    try {
      setShowAlert(false);
      const { code } = data;
      verifySignupCode({ body: { code: code.trim() } })
        .then((res: any) => {
          if (res?.data?.ok) {
            setSignUpCode(code.trim());
          } else {
            const errorMessage = res?.error?.data?.message;
            setShowAlert(true);
            setAlertData({
              type: 'error',
              message:
                t(`serverMessages.${errorMessage}`) ||
                t('serverMessages.SERVER-ERROR')
            });
          }
        })
        .catch((error: any) => {
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message:
              t(`serverMessages.${error?.data?.message}`) ||
              t('serverMessages.SERVER-ERROR')
          });
        });
    } catch (error: any) {
      setShowAlert(true);
      setAlertData({
        type: 'error',
        message: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginClick = () => {
    navigate('/');
  };
  return (
    <>
      {showAlert && (
        <Alert
          autoHide={false}
          onClose={() => setShowAlert(false)}
          {...alertData}
        />
      )}
      <div className="mb-4 md:mb-6">
        <div className="space-y-2 md:space-y-3 text-left">
          <h1 className="text-2xl md:text-3xl lg:text-[2rem] font-bold text-black leading-tight">
            {t('signUpCodeHead')}
          </h1>
          <p className="text-[#504F4F] text-base">{t('signUpCodeText')}</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-3 md:space-y-4"
        noValidate>
        <Input
          name="code"
          label={t('signUpCode')}
          placeholder={t('signUpCodePlaceholder')}
          {...register('code')}
          required
          error={errors.code?.message}
        />

        <div className="space-y-3 md:space-y-4 pt-2">
          <Button
            btnStyle="primary"
            testid="exit-button"
            type="submit"
            loading={isLoading}
            disabled={isLoading}
            className="rounded-lg w-full py-3">
            {t('continue')}
          </Button>

          <div className="text-center">
            <Button
              btnStyle="link"
              onClick={handleLoginClick}
              className="text-sm font-semibold text-[#504F4F]">
              {t('alreadyAccount')}{' '}
              <span className="text-[var(--na-primary-color)] hover:text-[var(--na-primary-color)] underline">
                {t('login')}
              </span>
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};
